/**
 * Type definitions and utilities for Recharts 3.x integration.
 *
 * Recharts 3.x changed the event handler signatures. This module provides
 * type-safe wrappers and utilities for working with Recharts events.
 *
 * @see https://github.com/recharts/recharts/wiki/3.0-migration-guide
 */
import type { MouseEvent, ReactElement } from 'react';
import type { BarRectangleItem } from 'recharts';
import type { LabelContentType } from 'recharts/types/component/Label';
import type { CartesianLabelListEntry, PolarLabelListEntry } from 'recharts/types/component/LabelList';
import type { PayloadChart, ToolTipElement } from '../../shared/widget-data.interfaces';
import type { CustomModalProps } from '../CustomDetailsWindow/custom-details-window.component';
import type { ColumnChartCustomModalProps } from './column-chart.component';

/**
 * Recharts 3.x BarRectangleItem with typed payload.
 * The payload contains the original data item from your chart data array.
 */
export interface BarClickData<TPayload = Record<string, unknown>> extends Omit<BarRectangleItem, 'payload'> {
  /**
   * The original data item from the chart's data array.
   * This is the record that corresponds to this bar.
   */
  payload: TPayload;
}

/**
 * Type for the onClick handler of a Bar component in Recharts 3.x.
 */
export type BarClickHandler<TPayload = Record<string, unknown>> = (
  data: BarClickData<TPayload>,
  index: number,
  event: MouseEvent<SVGPathElement>
) => void;

/**
 * Payload structure for chart data that includes modal fields.
 * Used when clicking on bars to open the details modal.
 */
export interface ChartDataPayload {
  field?: string;
  name?: string;
  secondField?: string;
  recordIds?: string[];
  tooltipPayload?: Array<{ name: string; dataKey: string }>;
  [key: string]: unknown;
}

/**
 * Extracts modal props from a Recharts 3.x Bar onClick event.
 * Provides type-safe access to the payload data needed for the details modal.
 *
 * @param data - The BarRectangleItem from the onClick event
 * @returns CustomModalProps-compatible object for opening the details modal
 */
export function extractModalPropsFromBarClick(data: BarRectangleItem): CustomModalProps {
  const payload = data.payload as ChartDataPayload | undefined;

  return {
    field: payload?.field,
    name: payload?.name ?? '',
    tooltipPayload: payload?.tooltipPayload ?? [],
    fieldValue: payload?.field,
    secondFieldValue: payload?.secondField,
    recordIds: payload?.recordIds,
  };
}

/**
 * Extracts column chart modal props from a Recharts 3.x Bar onClick event.
 * Similar to extractModalPropsFromBarClick but includes Sum field.
 *
 * @param data - The BarRectangleItem from the onClick event
 * @returns ColumnChartCustomModalProps-compatible object for opening the details modal
 */
export function extractColumnChartModalProps(data: BarRectangleItem): ColumnChartCustomModalProps {
  const payload = data.payload as (ChartDataPayload & { Sum?: string }) | undefined;

  return {
    ...extractModalPropsFromBarClick(data),
    Sum: payload?.Sum,
  };
}

/**
 * Creates a type-safe onClick handler for Bar components that opens a modal.
 *
 * @param onShowModal - The modal show function
 * @returns A properly typed onClick handler for the Bar component
 *
 * @example
 * ```tsx
 * <Bar
 *   onClick={createBarClickHandler(onShowModal)}
 * />
 * ```
 */
export function createBarClickHandler(onShowModal: (data: CustomModalProps) => void): (data: BarRectangleItem) => void {
  return (data: BarRectangleItem) => {
    onShowModal(extractModalPropsFromBarClick(data));
  };
}

/**
 * Creates a type-safe onClick handler for Column Chart Bar components.
 *
 * @param onShowModal - The modal show function
 * @returns A properly typed onClick handler for the Bar component
 */
export function createColumnChartBarClickHandler(
  onShowModal: (data: ColumnChartCustomModalProps) => void
): (data: BarRectangleItem) => void {
  return (data: BarRectangleItem) => {
    onShowModal(extractColumnChartModalProps(data));
  };
}

// ============================================================================
// LabelList Content Type Adapters
// ============================================================================

/**
 * Recharts 3.x LabelList content prop type.
 * This is what Recharts passes to the content function.
 */
export type LabelListContentProps = CartesianLabelListEntry | PolarLabelListEntry;

/**
 * Validates that props have the expected CartesianLabelListEntry shape.
 * Used to detect if Recharts changes its internal behavior in future versions.
 *
 * @param props - The props to validate
 * @returns true if props appear to be a valid CartesianLabelListEntry
 */
function isCartesianLabelListEntry(props: unknown): props is CartesianLabelListEntry {
  if (props === null || typeof props !== 'object') {
    return false;
  }
  const obj = props as Record<string, unknown>;
  // CartesianLabelListEntry should have x, y as numbers (or undefined)
  // and typically has width, height for bar charts
  const hasCoordinates = typeof obj.x === 'number' || typeof obj.y === 'number';
  const hasValue = 'value' in obj;
  return hasCoordinates || hasValue;
}

/**
 * Logs a warning when unexpected props are received from Recharts.
 * This helps detect breaking changes in Recharts internals.
 *
 * @internal Only called from wrapper functions that have already checked import.meta.env.DEV
 */
function warnUnexpectedProps(fnName: string, props: unknown): void {
  console.warn(
    `[recharts-types] ${fnName}: Received unexpected props structure from Recharts. ` +
      `This may indicate a breaking change in Recharts. Props:`,
    props
  );
}

/**
 * Adapts Recharts 3.x LabelList content props to the legacy PayloadChart type.
 *
 * The Recharts 3.x LabelListEntry has a different structure than our legacy PayloadChart type.
 * This adapter maps the new structure to the expected format.
 *
 * @internal This is an internal adapter function - do not call directly from component code.
 * Use {@link wrapPayloadChartContent} instead, which handles validation.
 *
 * @param props - The Recharts 3.x LabelList content props (must be pre-validated)
 * @returns PayloadChart-compatible object
 *
 * @remarks
 * **Validation Contract**: This function must only be called by wrapper functions that
 * have already validated the props structure. Direct calls from component code are unsafe.
 *
 * The cast to CartesianLabelListEntry is safe because:
 * 1. {@link wrapPayloadChartContent} validates props via {@link isCartesianLabelListEntry} in development
 * 2. In production, Recharts consistently provides CartesianLabelListEntry for bar/line charts
 * 3. All accessed properties (x, y, width, height) use nullish coalescing as a safety net
 */
function adaptToPayloadChart(props: LabelListContentProps): PayloadChart {
  // Cast is safe: caller (wrapper function) validates via isCartesianLabelListEntry
  const cartesian = props as CartesianLabelListEntry;
  const payload = (props.payload ?? {}) as Record<string, unknown>;

  return {
    name: (payload.name as string) ?? '',
    fill: props.fill ?? '',
    x: cartesian.x ?? 0,
    y: cartesian.y ?? 0,
    width: cartesian.width ?? 0,
    height: cartesian.height ?? 0,
    value: Array.isArray(props.value) ? props.value.map(Number) : [Number(props.value ?? 0)],
    payload: {
      ...payload,
      name: (payload.name as string) ?? '',
      color: (payload.color as string) ?? props.fill ?? '',
    },
    background: {
      x: cartesian.x ?? 0,
      y: cartesian.y ?? 0,
      width: cartesian.width ?? 0,
      height: cartesian.height ?? 0,
    },
    position: '',
  };
}

/**
 * Adapts Recharts 3.x LabelList content props to the legacy ToolTipElement type.
 *
 * @internal This is an internal adapter function - do not call directly from component code.
 * Use {@link wrapToolTipElementContent} instead, which handles validation.
 *
 * @param props - The Recharts 3.x LabelList content props (must be pre-validated)
 * @returns ToolTipElement-compatible object (partial, with essential fields)
 *
 * @remarks
 * **Validation Contract**: This function must only be called by wrapper functions that
 * have already validated the props structure. Direct calls from component code are unsafe.
 *
 * The cast to CartesianLabelListEntry is safe because:
 * 1. {@link wrapToolTipElementContent} validates props via {@link isCartesianLabelListEntry} in development
 * 2. In production, Recharts consistently provides CartesianLabelListEntry for bar/line charts
 * 3. All accessed properties use nullish coalescing as a safety net
 */
function adaptToToolTipElement(props: LabelListContentProps): ToolTipElement {
  // Cast is safe: caller (wrapper function) validates via isCartesianLabelListEntry
  const cartesian = props as CartesianLabelListEntry;
  const payload = props.payload as Record<string, unknown> | undefined;

  return {
    x: cartesian.x ?? 0,
    y: cartesian.y ?? 0,
    cx: 0,
    cy: 0,
    outerRadius: 0,
    value: typeof props.value === 'number' ? props.value : Number(props.value ?? 0),
    index: 0,
    endAngle: 0,
    startAngle: 0,
    labelFormat: '',
    labelDecimals: 0,
    fill: props.fill,
    payload: payload
      ? {
          dataKey: '',
          name: (payload.name as string) ?? '',
          field: (payload.field as string) ?? '',
          value: String(props.value ?? ''),
          color: (payload.color as string) ?? props.fill ?? '',
          fill: props.fill ?? '',
        }
      : undefined,
    tooltipPosition: {
      x: cartesian.x ?? 0,
      y: cartesian.y ?? 0,
    },
  };
}

/**
 * Creates a wrapper for CustomMiddleLabelList that accepts Recharts 3.x content props.
 *
 * @param renderFn - The original render function expecting PayloadChart
 * @returns A function compatible with Recharts 3.x LabelList content prop
 *
 * @remarks
 * In development, validates props and logs a warning if they don't match expected
 * CartesianLabelListEntry shape. In production, trusts Recharts provides correct
 * props to avoid validation overhead on every render.
 *
 * @example
 * ```tsx
 * <LabelList
 *   content={wrapPayloadChartContent(CustomMiddleLabelList)}
 * />
 * ```
 */
export function wrapPayloadChartContent(renderFn: (props: PayloadChart) => ReactElement | null): LabelContentType {
  return ((props: unknown) => {
    // Only validate in development - Vite removes this branch in production builds
    if (import.meta.env.DEV && !isCartesianLabelListEntry(props)) {
      warnUnexpectedProps('wrapPayloadChartContent', props);
      return null;
    }
    // In production, trust props are valid but adapter still uses safe defaults
    return renderFn(adaptToPayloadChart(props as LabelListContentProps));
  }) as LabelContentType;
}

/**
 * Creates a wrapper for renderCustomizedLineLabel that accepts Recharts 3.x content props.
 *
 * @param renderFn - The original render function expecting ToolTipElement
 * @returns A function compatible with Recharts 3.x LabelList content prop
 *
 * @remarks
 * In development, validates props and logs a warning if they don't match expected
 * CartesianLabelListEntry shape. In production, trusts Recharts provides correct
 * props to avoid validation overhead on every render.
 *
 * @example
 * ```tsx
 * <LabelList
 *   content={wrapToolTipElementContent(renderCustomizedLineLabel)}
 * />
 * ```
 */
export function wrapToolTipElementContent(
  renderFn: (props: ToolTipElement) => ReactElement | null | undefined
): LabelContentType {
  return ((props: unknown) => {
    // Only validate in development - Vite removes this branch in production builds
    if (import.meta.env.DEV && !isCartesianLabelListEntry(props)) {
      warnUnexpectedProps('wrapToolTipElementContent', props);
      return null;
    }
    // In production, trust props are valid but adapter still uses safe defaults
    return renderFn(adaptToToolTipElement(props as LabelListContentProps)) ?? null;
  }) as LabelContentType;
}
